/**
 * Liquid Glass (variant B): closing animations for surfaces that React simply
 * removes.
 *
 * A glass surface that closes by unmounting has no way to animate out — the
 * search panel, the full-screen chat card, a sheet a caller closes straight
 * from its Save. Tom, on his phone (2026-10-10): they "just disappear". So at
 * the moment of closing we leave a GHOST behind: a copy of what was on screen,
 * on the same spot, that plays the close and removes itself. The real state
 * changes immediately, exactly as before; nothing waits on the animation.
 *
 * Glass only, and never under reduced motion. Variant A keeps its behaviour.
 */
import { UI_GLASS } from '@/lib/ui-variant/shared';
import { prefersReducedMotion } from './genie';

export const isGlassDocument = (): boolean =>
  typeof document !== 'undefined' && document.documentElement.dataset.ui === UI_GLASS;

/** Whether a ghost should run at all right now. */
export const glassExitsOn = (): boolean => isGlassDocument() && !prefersReducedMotion();

/**
 * Copy `el` (with what the person had typed and how far they had scrolled,
 * which `cloneNode` leaves behind) into `parent`, inert, and hand it back.
 */
function ghostOf(el: HTMLElement, parent: HTMLElement): HTMLElement {
  const ghost = el.cloneNode(true) as HTMLElement;
  const from = el.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
  const to = ghost.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
  from.forEach((f, i) => { if (to[i] && f.type !== 'file') to[i].value = f.value; });
  ghost.removeAttribute('id');
  ghost.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
  ghost.setAttribute('aria-hidden', 'true');
  ghost.setAttribute('inert', '');
  ghost.style.pointerEvents = 'none';
  parent.appendChild(ghost);
  const scrolled = el.querySelectorAll<HTMLElement>('*');
  const copies = ghost.querySelectorAll<HTMLElement>('*');
  scrolled.forEach((s, i) => { if (s.scrollTop && copies[i]) copies[i].scrollTop = s.scrollTop; });
  return ghost;
}

function play(ghost: HTMLElement, target: HTMLElement, frames: Keyframe[], options: KeyframeAnimationOptions): void {
  let gone = false;
  const remove = () => { if (!gone) { gone = true; ghost.remove(); } };
  try {
    const anim = target.animate(frames, { fill: 'forwards', ...options });
    anim.addEventListener('finish', remove);
    anim.addEventListener('cancel', remove);
  } catch {
    remove();
    return;
  }
  // Backstop: a ghost must never outlive its animation, whatever the engine does.
  setTimeout(remove, Number(options.duration ?? 400) + 200);
}

/**
 * The search panel folds back up into the pill it dropped out of. Copied into
 * the same parent, so every rule that styled it still applies.
 */
export function foldIntoPill(panel: HTMLElement | null): void {
  if (!panel?.parentElement || !glassExitsOn()) return;
  const ghost = ghostOf(panel, panel.parentElement);
  ghost.style.animation = 'none';
  play(ghost, ghost, [
    { transform: 'scale(1)', opacity: 1 },
    { transform: 'scale(0.3)', opacity: 0 },
  ], { duration: 240, easing: 'cubic-bezier(0.4, 0, 0.6, 1)' });
}

/**
 * The full-screen chat card shrinks back into the M orb it grew out of. The
 * card is a pseudo-element of the bar sheet, so this draws a plain dark card on
 * the same rectangle — the conversation itself has already gone, which is what
 * a closing card looks like anyway.
 */
export function collapseIntoOrb(sheet: HTMLElement | null): void {
  if (!sheet || !glassExitsOn()) return;
  const r = sheet.getBoundingClientRect();
  if (r.height < 120) return; // not the full-screen card
  const padB = parseFloat(getComputedStyle(sheet).getPropertyValue('--glass-pad-b')) || 14;
  const card = document.createElement('div');
  card.className = 'mglass-exit-card';
  card.setAttribute('aria-hidden', 'true');
  Object.assign(card.style, {
    left: `${r.left}px`,
    top: `${r.top}px`,
    width: `${r.width}px`,
    height: `${r.height}px`,
    transformOrigin: `${r.width - 45}px ${r.height - padB - 31}px`,
  });
  document.body.appendChild(card);
  play(card, card, [
    { transform: 'scale(1)', borderRadius: '0px', opacity: 1 },
    { transform: 'scale(0.12)', borderRadius: '260px', opacity: 1, offset: 0.85 },
    { transform: 'scale(0.1)', borderRadius: '300px', opacity: 0 },
  ], { duration: 380, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' });
}

/**
 * A sheet removed without going through its own close (a caller unmounting it
 * from Save) slides down instead of vanishing. `backdrop` is the
 * `.mucka-mod-backdrop` holding the sheet; the copy goes on <body>, where every
 * `.mucka-mod-*` rule still reaches it.
 */
export function slideSheetAway(backdrop: HTMLElement | null, durationMs: number, easing: string): void {
  if (!backdrop?.isConnected || !glassExitsOn()) return;
  const ghost = ghostOf(backdrop, document.body);
  ghost.style.animation = 'none';
  const sheet = ghost.querySelector<HTMLElement>('.mucka-mod-sheet');
  if (sheet) {
    sheet.style.animation = 'none';
    sheet.dataset.moving = '1';
    play(ghost, sheet, [{ transform: 'translateY(0)' }, { transform: 'translateY(100%)' }], { duration: durationMs, easing });
  }
  play(ghost, ghost, [{ opacity: 1 }, { opacity: 0 }], { duration: durationMs, easing: 'ease-in' });
}
